<template>
    <div v-if="showLearnHowDrawer">
        <component :is="component" v-if="localized" v-bind="$props" v-on="$listeners" />
        <div v-else>
            <v-navigation-drawer v-model="showLearnHowDrawer" class="navigation-drawer" fixed right temporary>
                <v-row>
                    <v-col>
                        <div class="header">
                            <h4 class="primary-color header-title">Financing Information</h4>
                            <div class="close-margin">
                                <v-icon color="primary" @click="learnHowClose">$vuetify.icons.times</v-icon>
                            </div>
                        </div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <div v-if="fixedRate" class="header">
                            <h5 class="primary-color header-title" style="font-size: 16px">
                                Fixed Pay Financing for {{ learnHowPaymentPlan.durationMonths }} months
                            </h5>
                        </div>
                        <div v-else class="header">
                            <h5 class="primary-color header-title" style="font-size: 16px">
                                Promo Financing for {{ learnHowPaymentPlan.durationMonths }} months
                            </h5>
                        </div>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="padding-none">
                        <div class="box">
                            <div v-if="fixedRate" class="body-1-lg primary-color bold">
                                Total Payments With Interest
                            </div>
                            <div v-else class="body-1-lg primary-color bold">Amount Financed</div>
                            <h2 v-if="fixedRate">{{ learnHowPaymentPlan.totalCostCents | currencyWhole }}</h2>
                            <h2 v-else>{{ purchaseAmount | currencyWhole }}</h2>
                        </div>
                    </v-col>
                </v-row>
                <v-row height="185px">
                    <v-col class="padding-none">
                        <div class="box">
                            <div v-if="fixedRate" class="body-1-lg bold">Required Payments</div>
                            <div v-else class="body-1-lg bold">Suggested Payments</div>
                            <h2>{{ learnHowPaymentPlan.monthlyAmountCents | currencyWhole }}/mo</h2>
                        </div>
                    </v-col>
                </v-row>
                <v-row v-if="learnHowPaymentPlan.durationMonths" height="185px">
                    <v-col class="padding-none">
                        <div class="box">
                            <div v-if="fixedRate" class="body-1-lg bold">Promotional Period</div>
                            <div v-else class="body-1-lg bold">Payoff Period</div>
                            <h2>{{ learnHowPaymentPlan.durationMonths }} Months</h2>
                        </div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <div class="margin body-1">
                            <div
                                v-if="fixedRate"
                                style="text-align: left"
                                v-html="
                                    $t('synchrony_standard.plan.type.fixed_rate.learn-how-details', {
                                        months: learnHowPaymentPlan.durationMonths,
                                        apr: learnHowPaymentPlan.apr.toFixed(2),
                                        multiplier: multiplierPercent,
                                    })
                                "
                            ></div>
                            <div
                                v-else
                                style="text-align: left"
                                v-html="
                                    $t('synchrony_standard.plan.type.deferred_interest.learn-how-details', {
                                        months: learnHowPaymentPlan.durationMonths,
                                    })
                                "
                            ></div>
                        </div>
                    </v-col>
                </v-row>
            </v-navigation-drawer>
        </div>
    </div>
</template>

<script>
    import { mapState } from "vuex"
    import { providerState } from "@/state"
    import LocalizedComponentMixin from "@/components/localization/LocalizedComponentMixin"

    export default {
        name: "LearnHowDrawer",
        mixins: [LocalizedComponentMixin],
        props: {
            value: {
                type: Boolean,
                default: false,
            },
            learnHowPaymentPlan: {
                type: Object,
            },
        },
        data() {
            return {
                componentType: "learnHowDrawer",
            }
        },
        computed: {
            ...mapState("application", {
                application: state => state.data,
            }),

            showLearnHowDrawer: {
                get() {
                    return this.value
                },

                set(value) {
                    this.$emit("input", value)
                },
            },
            purchaseAmount() {
                const approvedAmount = providerState.getPreAuthResponse().approvedAmountCents
                return Math.min(this.application.purchaseAmountCents, approvedAmount)
            },
            aprCheck() {
                return this.learnHowPaymentPlan.durationMonths > 19
            },
            multiplierPercent() {
                switch (this.learnHowPaymentPlan.durationMonths) {
                    case 24:
                        return "4.9876"
                    case 36:
                        return "3.6605"
                    case 48:
                        return "3.0377"
                    case 60:
                        return "2.6997"
                    default:
                        return ""
                }
            },
            fixedRate() {
                return this.learnHowPaymentPlan.typeId === "fixed_rate"
            },
        },

        mounted() {
            globalThis.addEventListener("keydown", this.handleEscapeKey)
        },

        beforeDestroy() {
            globalThis.removeEventListener("keydown", this.handleEscapeKey)
        },

        methods: {
            handleEscapeKey(event) {
                if (event.key === "Escape" && this.showLearnHowDrawer) {
                    this.learnHowClose()
                }
            },

            learnHowClose() {
                this.$emit("input", false)
            },
        },
    }
</script>

<style lang="scss" scoped>
    .box {
        height: 104px;
        background: #f2f3f4;
        border-radius: 8px;
        margin: 16px;
        color: $primary-color;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        @media #{map-get($display-breakpoints, "sm-and-up")} {
            width: 524px;
        }
    }

    .navigation-drawer {
        width: 560px !important;
        font-family: MessinaSansWeb, Serif, serif;
    }

    .margin {
        margin: 16px;
    }

    .header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    .close-margin {
        margin-right: 20px;
    }

    .header-title {
        padding-left: 16px;
        font-weight: 600;
    }

    .container-center {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .bold {
        font-weight: bold;
    }

    .padding-none {
        padding-bottom: 0;
        padding-top: 0;
    }

    .row {
        margin-top: 0;
    }
</style>
